import { css } from '@emotion/react';
import { UserLayout } from '@web/features/layouts/user';
import {
  fadeInDown,
  listAnimation,
  listChildAnimation,
  theme,
} from '@web/features/theme';
import { profiles } from '@web/utils/tilt-profiles';
import { motion } from 'framer-motion';
import Image from 'next/image';

/**
 * # AboutPage
 * @packageDescription
 *
 * This page serves to inform a user about the TILT Team and the TILT Mission
 *
 * Pictures for the profile are located in `public/images`
 * Text can be found and edited in the {@link profiles} object above
 */
function AboutPage() {
  return (
    <UserLayout withHomeButton>
      <motion.h1
        variants={fadeInDown}
        css={{ flex: '1 1 100%', paddingTop: '2%' }}
      >
        Learn more about the TILT Team
      </motion.h1>

      <div css={styles.container}>
        <motion.ul variants={listAnimation}>
          {[...profiles]
            .sort((a, b) => (a.name > b.name ? 1 : -1))
            .map((profile) => (
              <motion.li key={profile.name} variants={listChildAnimation}>
                <div
                  style={{
                    position: 'relative',
                    width: '100%',
                    height: '300px',
                    marginBottom: '1em',
                    blockOverflow: 'hidden',
                  }}
                >
                  <Image
                    src={profile.imageSrc}
                    alt={profile.name}
                    sizes="400px"
                    fill
                    style={{
                      objectFit: 'cover',
                    }}
                  />
                </div>

                <div>
                  <h2>{profile.name}</h2>

                  <p>{profile.title}</p>

                  <p>{profile.body}</p>
                </div>
              </motion.li>
            ))}
        </motion.ul>
      </div>
    </UserLayout>
  );
}

const styles = {
  container: css`
    li {
      background-color: ${theme.colors.whiteAlpha[300]};
      padding: ${theme.space[4]} ${theme.space[8]};
      border-radius: ${theme.radii.lg};
      margin: ${theme.space[8]} auto;
      display: block;
      text-align: center;

      .image {
        display: block;
        grid-area: image;
        margin: 0 auto ${theme.space[4]};
        background-color: ${theme.colors.whiteAlpha[300]};
        border-radius: ${theme.radii.lg};
        max-height: ${theme.space[48]};
        max-width: ${theme.space[64]};
      }

      @media (min-width: 800px) {
        text-align: left;
        display: grid;
        grid-template-columns: 1fr 2fr;
        grid-template-areas: 'image body';
        grid-column-gap: ${theme.space[4]};
        align-items: center;
        jusitfy-items: center;
        jusitfy-content: center;

        .image {
          display: block;
          margin: 0 auto;
        }
      }
    }

    p {
      line-height: ${theme.lineHeights.tall};
      margin: ${theme.space[2]} 0;
      max-width: 80ch;

      &:nth-child(2n + 0) {
        font-style: italic;
      }
    }

    a {
      color: ${theme.colors.info};
    }
  `,
};

export default AboutPage;
